import { createSlice } from "@reduxjs/toolkit";
import {
  createSec17InterimOrdersAction,
  getSec17InterimOrdersByCaseIDAction,
  updateSec17InterimOrdersAction,
  deleteSec17InterimOrdersAction,
  createSec17RevocationRequestAction,
  getSec17RevocationRequestByCaseIDAction,
  updateSec17RevocationRequestAction,
  deleteSec17RevocationRequestAction,
  deleteSec17RevocationRequestDocumentAction,
} from "../../actions/Sec17InterimOrders/sec17InterimOrdersAction";

const initialState = {
  interimOrders: [],
  revocationRequests: [],
  loading: false,
  error: null,
  success: false,
  successMessage: "",
};

const sec17InterimOrdersSlice = createSlice({
  name: "sec17InterimOrders",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
      state.successMessage = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSec17InterimOrdersByCaseIDAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getSec17InterimOrdersByCaseIDAction.fulfilled,
        (state, action) => {
          state.loading = false;
          state.interimOrders = action.payload.Sec17InterimOrders || [];
        },
      )
      .addCase(
        getSec17InterimOrdersByCaseIDAction.rejected,
        (state, action) => {
          state.loading = false;
          state.error = action.payload;
        },
      );

    // =========================================================================
    // CREATE INTERIM ORDER
    // =========================================================================
    builder
      .addCase(createSec17InterimOrdersAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSec17InterimOrdersAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.successMessage = "Interim Order created successfully";
        state.interimOrders.push(action.payload.data);
      })
      .addCase(createSec17InterimOrdersAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // =========================================================================
    // UPDATE INTERIM ORDER
    // =========================================================================
    builder
      .addCase(updateSec17InterimOrdersAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSec17InterimOrdersAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.successMessage = "Interim Order updated successfully";

        const index = state.interimOrders.findIndex(
          (o) =>
            o.sec17InterimOrders_ID === action.payload.sec17InterimOrders_ID,
        );

        if (index !== -1) {
          state.interimOrders[index] = action.payload;
        }
      })
      .addCase(updateSec17InterimOrdersAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // =========================================================================
    // DELETE INTERIM ORDER
    // =========================================================================
    builder
      .addCase(deleteSec17InterimOrdersAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSec17InterimOrdersAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.successMessage = "Interim Order deleted successfully";

        state.interimOrders = state.interimOrders.filter(
          (o) => o.sec17InterimOrders_ID !== action.payload,
        );
      })
      .addCase(deleteSec17InterimOrdersAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // =========================================================================
    // GET REVOCATION REQUESTS BY CASE ID
    // =========================================================================
    builder
      .addCase(getSec17RevocationRequestByCaseIDAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getSec17RevocationRequestByCaseIDAction.fulfilled,
        (state, action) => {
          state.loading = false;
          state.revocationRequests = action.payload.data || [];
        },
      )
      .addCase(
        getSec17RevocationRequestByCaseIDAction.rejected,
        (state, action) => {
          state.loading = false;
          state.error = action.payload;
        },
      );

    // =========================================================================
    // CREATE REVOCATION REQUEST
    // =========================================================================
    builder
      .addCase(createSec17RevocationRequestAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createSec17RevocationRequestAction.fulfilled,
        (state, action) => {
          state.loading = false;
          state.success = true;
          state.successMessage = "Revocation request created successfully";
          state.revocationRequests.push(action.payload);
        },
      )
      .addCase(createSec17RevocationRequestAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // =========================================================================
    // UPDATE REVOCATION REQUEST
    // =========================================================================
    builder
      .addCase(updateSec17RevocationRequestAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateSec17RevocationRequestAction.fulfilled,
        (state, action) => {
          state.loading = false;
          state.success = true;
          state.successMessage = "Revocation request updated successfully";

          const index = state.revocationRequests.findIndex(
            (r) =>
              r.sec17RevocationRequest_ID ===
              action.payload.sec17RevocationRequest_ID,
          );

          if (index !== -1) {
            state.revocationRequests[index] = action.payload;
          }
        },
      )
      .addCase(updateSec17RevocationRequestAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // =========================================================================
    // DELETE REVOCATION REQUEST
    // =========================================================================
    builder
      .addCase(deleteSec17RevocationRequestAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteSec17RevocationRequestAction.fulfilled,
        (state, action) => {
          state.loading = false;
          state.success = true;
          state.successMessage = "Revocation request deleted successfully";

          state.revocationRequests = state.revocationRequests.filter(
            (r) => r.sec17RevocationRequest_ID !== action.payload,
          );
        },
      )
      .addCase(deleteSec17RevocationRequestAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // =========================================================================
    // DELETE REVOCATION REQUEST DOCUMENT
    // =========================================================================
    builder
      .addCase(deleteSec17RevocationRequestDocumentAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteSec17RevocationRequestDocumentAction.fulfilled,
        (state) => {
          state.loading = false;
          state.success = true;
          state.successMessage = "Document deleted successfully";
        },
      )
      .addCase(
        deleteSec17RevocationRequestDocumentAction.rejected,
        (state, action) => {
          state.loading = false;
          state.error = action.payload;
        },
      );
  },
});

export const { clearError, clearSuccess } = sec17InterimOrdersSlice.actions;

export default sec17InterimOrdersSlice.reducer;
